import { useState, useRef, useCallback, useEffect } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  useSensor,
  useSensors,
  PointerSensor,
  useDraggable,
} from '@dnd-kit/core';
import type { TableInfo, TableStatus } from '@/types/api';
import { Save, RotateCcw, AlertTriangle, Square, Circle } from 'lucide-react';
import TableCanvasPopup from './TableCanvasPopup';
import { mergeTablesForDisplay, type PendingTableOp } from './tableCanvasPendingOps';

export type { PendingTableOp } from './tableCanvasPendingOps';

const DEFAULT_W = 120;
const DEFAULT_H = 100;
const OUT_OF_BOUNDS_MARGIN = 200;

const SHAPES = ['rectangle', 'round', 'triangle', 'hexagon'] as const;
type TableShape = typeof SHAPES[number];
const MIN_W = 70;
const MIN_H = 60;

export interface TablePosition {
  tableId: number;
  x: number;
  y: number;
  rotation: number;
  width: number;
  height: number;
  shape?: string;
}

export interface LayoutOption {
  id: number;
  name: string;
  isDefault?: boolean;
  shiftName?: string;
}

interface TableLayoutEditorProps {
  tables: TableInfo[];
  initialPositions?: TablePosition[];
  /** Must resolve on a successful save and reject on failure - the editor only
   *  clears its dirty flag once this settles, so a failed save leaves the Save
   *  button enabled and local edits in place instead of looking "clean". pendingOps
   *  carries staged add/detach/delete actions from the canvas popup -- see
   *  tableCanvasPendingOps.ts -- to be applied by the caller before the ordinary
   *  position/shape save. */
  onSave: (positions: TablePosition[], pendingOps: PendingTableOp[]) => Promise<void>;
  backgroundImage?: { base64: string; contentType: string } | null;
  designWidth?: number;
  designHeight?: number;
  layouts?: LayoutOption[];
  activeLayoutId?: number;
  onLayoutChange?: (id: number) => void;
  /** Gates the Save/Reset toolbar. Callers without floor.update should pass false. Defaults to true. */
  canSave?: boolean;
  /** Notified whenever the local dirty flag changes, so the parent can warn
   *  before route navigation or tab close discards unsaved edits. */
  onDirtyChange?: (isDirty: boolean) => void;
  /** Tables with no POS floor -- offered in the "add unassigned table" canvas popup. */
  unassignedTables?: TableInfo[];
  loadingUnassignedTables?: boolean;
}

function seedPositions(tables: TableInfo[], initialPositions: TablePosition[]): TablePosition[] {
  const positionMap = new Map(initialPositions.map((p) => [p.tableId, p]));

  return tables.map((table, index) => {
    if (positionMap.has(table.id)) {
      const saved = positionMap.get(table.id)!;
      return {
        ...saved,
        width: saved.width ?? DEFAULT_W,
        height: saved.height ?? DEFAULT_H,
      };
    }
    const gridCols = 6;
    const spacing = 150;
    return {
      tableId: table.id,
      x: (index % gridCols) * spacing + 50,
      y: Math.floor(index / gridCols) * spacing + 50,
      rotation: 0,
      width: DEFAULT_W,
      height: DEFAULT_H,
    };
  });
}

const statusConfig: Record<
  TableStatus,
  { color: string; bgColor: string; emoji: string }
> = {
  AVAILABLE: { color: 'text-green-800', bgColor: 'bg-green-100 border-green-300', emoji: '✓' },
  OCCUPIED: { color: 'text-red-800', bgColor: 'bg-red-100 border-red-300', emoji: '👥' },
  RESERVED: { color: 'text-blue-800', bgColor: 'bg-blue-100 border-blue-300', emoji: '📅' },
  CLEANING: { color: 'text-yellow-800', bgColor: 'bg-yellow-100 border-yellow-300', emoji: '🧹' },
  OUT_OF_SERVICE: { color: 'text-gray-800', bgColor: 'bg-gray-100 border-gray-300', emoji: '⚠️' },
};

export default function TableLayoutEditor({
  tables,
  initialPositions = [],
  onSave,
  backgroundImage,
  designWidth = 800,
  designHeight = 600,
  layouts,
  activeLayoutId,
  onLayoutChange,
  canSave = true,
  onDirtyChange,
  unassignedTables = [],
  loadingUnassignedTables = false,
}: TableLayoutEditorProps) {
  const [positions, setPositions] = useState<TablePosition[]>(() =>
    seedPositions(tables, initialPositions)
  );

  const [activeId, setActiveId] = useState<number | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [pendingOps, setPendingOps] = useState<PendingTableOp[]>([]);
  const [popup, setPopup] = useState<
    | { mode: 'empty'; x: number; y: number }
    | { mode: 'table'; x: number; y: number; tableId: number }
    | null
  >(null);
  const tempIdRef = useRef(-1);
  // A real drag always fires handleDragEnd (dnd-kit's 8px activation threshold means it never
  // fires for a plain click) -- set here and consumed by the next table click so that click
  // doesn't also open the popup right after the pointer is released from dragging.
  const justDraggedRef = useRef(false);

  const displayTables = mergeTablesForDisplay(tables, pendingOps);

  useEffect(() => {
    onDirtyChange?.(hasChanges);
  }, [hasChanges]);

  // Re-seed when fresh server data lands (e.g. a React Query refetch after a POS
  // full sync). State is initialized once on mount, so without this the editor
  // keeps showing stale shapes/positions from the cached pre-sync data.
  // Never clobber unsaved local edits.
  //
  // seededFingerprintRef guards a narrower race than "hasChanges" alone can:
  // right after a successful save, hasChanges flips to false, but the parent's
  // initialPositions prop still reflects the PRE-save cache until its React
  // Query invalidation actually refetches (invalidateQueries doesn't block the
  // mutation's resolution). If a render happens in that window, this effect
  // would otherwise reseed from stale pre-save data, visually reverting the
  // save that just succeeded. Only reseed once the fingerprint has genuinely
  // changed since the last one we accepted - i.e. once the refetch lands.
  const seedFingerprint = JSON.stringify([tables.map((t) => t.id), initialPositions]);
  const seededFingerprintRef = useRef(seedFingerprint);
  useEffect(() => {
    if (hasChanges) return;
    if (seedFingerprint === seededFingerprintRef.current) return;
    seededFingerprintRef.current = seedFingerprint;
    setPositions(seedPositions(tables, initialPositions));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedFingerprint, hasChanges]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as number);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, delta } = event;
    const tableId = active.id as number;
    setPositions((prev) =>
      prev.map((pos) =>
        pos.tableId === tableId
          ? { ...pos, x: Math.round(pos.x + delta.x), y: Math.round(pos.y + delta.y) }
          : pos
      )
    );
    setActiveId(null);
    setHasChanges(true);
    justDraggedRef.current = true;
  };

  const handleRotate = (tableId: number) => {
    setPositions((prev) =>
      prev.map((pos) =>
        pos.tableId === tableId
          ? { ...pos, rotation: (pos.rotation + 90) % 360 }
          : pos
      )
    );
    setHasChanges(true);
  };

  const handleResize = useCallback((tableId: number, width: number, height: number, x?: number, y?: number) => {
    setPositions((prev) =>
      prev.map((pos) => pos.tableId === tableId
        ? { ...pos, width, height, ...(x !== undefined ? { x } : {}), ...(y !== undefined ? { y } : {}) }
        : pos
      )
    );
    setHasChanges(true);
  }, []);

  const handleShapeChange = useCallback((tableId: number, shape: string) => {
    setPositions((prev) =>
      prev.map((pos) => pos.tableId === tableId ? { ...pos, shape } : pos)
    );
    setHasChanges(true);
  }, []);

  const handleReset = () => {
    const gridCols = 6;
    const spacing = 150;
    setPositions(
      displayTables.map((table, index) => ({
        tableId: table.id,
        x: (index % gridCols) * spacing + 50,
        y: Math.floor(index / gridCols) * spacing + 50,
        rotation: 0,
        width: DEFAULT_W,
        height: DEFAULT_H,
      }))
    );
    setHasChanges(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(positions, pendingOps);
      setHasChanges(false);
      setPendingOps([]);
    } catch {
      // Save failed - keep hasChanges true so the Save button stays enabled
      // and the reseed effect (still gated on hasChanges) won't overwrite the
      // user's edits. The parent is responsible for surfacing the error.
    } finally {
      setIsSaving(false);
    }
  };

  // Drops a not-yet-saved pending create/assign entry entirely, rather than staging a
  // meaningless detach/delete for a table that was never actually created.
  const removePendingEntry = (tempId: number) => {
    setPendingOps((prev) =>
      prev.filter((op) => !((op.type === 'create' || op.type === 'assign') && op.tempId === tempId))
    );
    setPositions((prev) => prev.filter((p) => p.tableId !== tempId));
  };

  const handleCanvasClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!canSave) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setPopup({
      mode: 'empty',
      x: Math.round(e.clientX - rect.left),
      y: Math.round(e.clientY - rect.top),
    });
  };

  const handleTableClick = (tableId: number) => {
    if (!canSave) return;
    if (justDraggedRef.current) {
      justDraggedRef.current = false;
      return;
    }
    const pos = positions.find((p) => p.tableId === tableId);
    setPopup({
      mode: 'table',
      x: (pos?.x ?? 0) + (pos?.width ?? DEFAULT_W) + 8,
      y: pos?.y ?? 0,
      tableId,
    });
  };

  const handleCreateNew = (name: string, capacity: number) => {
    if (!popup || popup.mode !== 'empty') return;
    const tempId = tempIdRef.current--;
    setPendingOps((prev) => [...prev, { type: 'create', tempId, name, capacity }]);
    setPositions((prev) => [
      ...prev,
      { tableId: tempId, x: popup.x, y: popup.y, rotation: 0, width: DEFAULT_W, height: DEFAULT_H },
    ]);
    setHasChanges(true);
    setPopup(null);
  };

  const handleAddUnassigned = (table: TableInfo) => {
    if (!popup || popup.mode !== 'empty') return;
    const tempId = tempIdRef.current--;
    setPendingOps((prev) => [
      ...prev,
      { type: 'assign', tempId, posTableId: table.posTableId ?? table.id, sourceTable: table },
    ]);
    setPositions((prev) => [
      ...prev,
      { tableId: tempId, x: popup.x, y: popup.y, rotation: 0, width: DEFAULT_W, height: DEFAULT_H },
    ]);
    setHasChanges(true);
    setPopup(null);
  };

  const handleDetachFromPopup = () => {
    if (!popup || popup.mode !== 'table') return;
    const { tableId } = popup;
    if (tableId < 0) {
      removePendingEntry(tableId);
    } else {
      setPendingOps((prev) => [...prev, { type: 'detach', tableId }]);
    }
    setHasChanges(true);
    setPopup(null);
  };

  const handleDeleteFromPopup = () => {
    if (!popup || popup.mode !== 'table') return;
    const { tableId } = popup;
    if (tableId < 0) {
      removePendingEntry(tableId);
    } else {
      setPendingOps((prev) => [...prev, { type: 'delete', tableId }]);
    }
    setHasChanges(true);
    setPopup(null);
  };

  const stagedPosTableIds = new Set(
    pendingOps
      .filter((op): op is Extract<PendingTableOp, { type: 'assign' }> => op.type === 'assign')
      .map((op) => op.posTableId)
  );
  const availableUnassignedTables = unassignedTables.filter(
    (t) => !stagedPosTableIds.has(t.posTableId ?? t.id)
  );

  const outOfBoundsTables = positions.filter(
    (pos) =>
      pos.x < 0 ||
      pos.y < 0 ||
      pos.x + pos.width > designWidth ||
      pos.y + pos.height > designHeight
  );

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between bg-white p-4 rounded-lg shadow-sm border border-gray-200 gap-4 flex-wrap">
        <div className="flex items-center gap-4 flex-wrap">
          {/* Layout selector — lives here so it's clear image controls are floor-level */}
          {layouts && layouts.length > 0 && onLayoutChange && (
            <div className="flex items-center gap-2">
              <label htmlFor="layout-select-editor" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                Layout:
              </label>
              <select
                id="layout-select-editor"
                value={activeLayoutId ?? ''}
                onChange={(e) => {
                  // Switching layouts remounts the editor (parent keys on
                  // activeLayoutId), which would otherwise silently discard
                  // any unsaved arrangement.
                  if (hasChanges && !window.confirm('You have unsaved changes. Switch layouts and discard them?')) {
                    // The browser already updated the native select's value;
                    // since we're not calling setState, React won't re-sync it
                    // back to the controlled value on its own - reset it here.
                    e.target.value = String(activeLayoutId ?? '');
                    return;
                  }
                  onLayoutChange(Number(e.target.value));
                }}
                className="block w-52 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm h-9 px-3"
              >
                {layouts.map((layout) => (
                  <option key={layout.id} value={layout.id}>
                    {layout.name}
                    {layout.isDefault ? ' (default)' : ''}
                    {layout.shiftName ? ` — ${layout.shiftName}` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {canSave && (
            <button onClick={handleReset} className="btn btn-outline h-9 text-sm">
              <RotateCcw className="h-4 w-4 mr-1" />
              Reset Layout
            </button>
          )}

          <span className="text-sm text-gray-400 hidden sm:inline">
            Drag to reposition · resize edges · hover to rotate · click empty space or a table for more options
          </span>
        </div>

        {canSave && (
          <button
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
            className="btn btn-primary h-9"
          >
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? 'Saving...' : 'Save Layout'}
          </button>
        )}
      </div>

      {/* Out-of-bounds warning */}
      {outOfBoundsTables.length > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {outOfBoundsTables.length === 1 ? 'Table' : 'Tables'}{' '}
            <strong>
              {outOfBoundsTables
                .map((pos) => displayTables.find((t) => t.id === pos.tableId)?.tableNumber ?? pos.tableId)
                .join(', ')}
            </strong>{' '}
            {outOfBoundsTables.length === 1 ? 'exceeds' : 'exceed'} the layout area (
            {designWidth}&nbsp;×&nbsp;{designHeight}). Drag{' '}
            {outOfBoundsTables.length === 1 ? 'it' : 'them'} back inside the boundary.
          </span>
        </div>
      )}

      {/* Canvas — scrollable wrapper; inner canvas = design area + out-of-bounds margin */}
      <div
        className="rounded-lg shadow-sm border border-gray-200 overflow-auto"
        style={{ maxHeight: 'calc(100vh - 300px)' }}
      >
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div
            style={{
              position: 'relative',
              width: designWidth + OUT_OF_BOUNDS_MARGIN,
              height: designHeight + OUT_OF_BOUNDS_MARGIN,
            }}
          >
            {/* Design area — white with image or grid; click on empty space opens the add-table popup */}
            <div
              onClick={canSave ? handleCanvasClick : undefined}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: designWidth,
                height: designHeight,
                background: 'white',
                overflow: 'hidden',
                cursor: canSave ? 'copy' : undefined,
              }}
            >
              {backgroundImage ? (
                <img
                  src={`data:${backgroundImage.contentType};base64,${backgroundImage.base64}`}
                  alt="Floor plan"
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  className="pointer-events-none select-none opacity-60"
                  draggable={false}
                />
              ) : (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    backgroundImage:
                      'linear-gradient(#e5e7eb 1px, transparent 1px), linear-gradient(90deg, #e5e7eb 1px, transparent 1px)',
                    backgroundSize: '50px 50px',
                  }}
                />
              )}
            </div>

            {/* Out-of-bounds zone: right strip — rgba(255,180,180,0.31) matches POS Color(255,180,180,80) */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: designWidth,
                width: OUT_OF_BOUNDS_MARGIN,
                height: designHeight + OUT_OF_BOUNDS_MARGIN,
                background: 'rgba(255,180,180,0.31)',
              }}
            />
            {/* Out-of-bounds zone: bottom strip */}
            <div
              style={{
                position: 'absolute',
                top: designHeight,
                left: 0,
                width: designWidth,
                height: OUT_OF_BOUNDS_MARGIN,
                background: 'rgba(255,180,180,0.31)',
              }}
            />

            {/* Dashed red boundary at layout dimensions */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: designWidth,
                height: designHeight,
                border: '2px dashed #dc2626',
                pointerEvents: 'none',
                zIndex: 50,
                boxSizing: 'border-box',
              }}
            />

            {/* Dimension label */}
            <span
              style={{
                position: 'absolute',
                top: designHeight + 6,
                left: 4,
                fontSize: 11,
                color: '#dc2626',
                fontWeight: 600,
                pointerEvents: 'none',
                zIndex: 50,
                whiteSpace: 'nowrap',
              }}
            >
              Layout boundary: {designWidth} × {designHeight}
            </span>

            {/* Tables */}
            {positions.map((pos) => {
              const table = displayTables.find((t) => t.id === pos.tableId);
              if (!table) return null;
              const outOfBounds =
                pos.x < 0 ||
                pos.y < 0 ||
                pos.x + pos.width > designWidth ||
                pos.y + pos.height > designHeight;
              return (
                <DraggableTable
                  key={table.id}
                  id={table.id}
                  position={pos}
                  table={table}
                  config={statusConfig[table.status]}
                  onRotate={handleRotate}
                  onResize={handleResize}
                  onShapeChange={handleShapeChange}
                  onClickTable={canSave ? handleTableClick : undefined}
                  isActive={activeId === table.id}
                  isOutOfBounds={outOfBounds}
                />
              );
            })}

            {popup && (
              <TableCanvasPopup
                x={popup.x}
                y={popup.y}
                mode={popup.mode}
                unassignedTables={availableUnassignedTables}
                loadingUnassigned={loadingUnassignedTables}
                onCreateNew={handleCreateNew}
                onAddUnassigned={handleAddUnassigned}
                onDetach={handleDetachFromPopup}
                onDelete={handleDeleteFromPopup}
                onClose={() => setPopup(null)}
              />
            )}
          </div>
        </DndContext>
      </div>
    </div>
  );
}

// ── Resize handle directions ────────────────────────────────────────────────

type ResizeDir = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

const HANDLE_SIZE = 8; // px, hit-target half-size

const handleStyle = (dir: ResizeDir, w: number, h: number): React.CSSProperties => {
  const half = HANDLE_SIZE / 2;
  const cursors: Record<ResizeDir, string> = {
    n: 'ns-resize', s: 'ns-resize',
    e: 'ew-resize', w: 'ew-resize',
    ne: 'nesw-resize', sw: 'nesw-resize',
    nw: 'nwse-resize', se: 'nwse-resize',
  };
  const base: React.CSSProperties = {
    position: 'absolute',
    cursor: cursors[dir],
    zIndex: 10,
    // Visible nub
    width: HANDLE_SIZE, height: HANDLE_SIZE,
    background: 'white',
    border: '2px solid #6366f1',
    borderRadius: 2,
  };
  switch (dir) {
    case 'n':  return { ...base, top: -half, left: w / 2 - half };
    case 's':  return { ...base, bottom: -half, left: w / 2 - half };
    case 'e':  return { ...base, right: -half, top: h / 2 - half };
    case 'w':  return { ...base, left: -half, top: h / 2 - half };
    case 'ne': return { ...base, top: -half, right: -half };
    case 'nw': return { ...base, top: -half, left: -half };
    case 'se': return { ...base, bottom: -half, right: -half };
    case 'sw': return { ...base, bottom: -half, left: -half };
  }
};

// ── Shape icon for picker ───────────────────────────────────────────────────

function ShapeIcon({ s }: { s: TableShape }) {
  if (s === 'round') return <Circle className="h-3 w-3 shrink-0" />;
  if (s === 'triangle') return (
    <svg viewBox="0 0 12 12" width="12" height="12" className="shrink-0" fill="currentColor">
      <polygon points="6,1 11,11 1,11" />
    </svg>
  );
  if (s === 'hexagon') return (
    <svg viewBox="0 0 12 12" width="12" height="12" className="shrink-0" fill="currentColor">
      <polygon points="6,0 11,3 11,9 6,12 1,9 1,3" />
    </svg>
  );
  return <Square className="h-3 w-3 shrink-0" />;
}

// ── DraggableTable ──────────────────────────────────────────────────────────

interface DraggableTableProps {
  id: number;
  position: TablePosition;
  table: TableInfo;
  config: { color: string; bgColor: string; emoji: string };
  onRotate: (id: number) => void;
  onResize: (id: number, width: number, height: number, x?: number, y?: number) => void;
  onShapeChange: (id: number, shape: string) => void;
  onClickTable?: (id: number) => void;
  isActive: boolean;
  isOutOfBounds: boolean;
}

function DraggableTable({
  id,
  position,
  table,
  config,
  onRotate,
  onResize,
  onShapeChange,
  onClickTable,
  isActive,
  isOutOfBounds,
}: DraggableTableProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [showShapeMenu, setShowShapeMenu] = useState(false);
  const shape = (position.shape ?? 'rectangle') as TableShape;

  useEffect(() => {
    if (!showShapeMenu) return;
    const close = () => setShowShapeMenu(false);
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [showShapeMenu]);
  const { attributes, listeners, setNodeRef, transform } = useDraggable({ id });

  const resizeState = useRef<{
    dir: ResizeDir;
    startX: number; startY: number;
    startW: number; startH: number;
    startPosX: number; startPosY: number;
  } | null>(null);

  const startResize = useCallback((e: React.PointerEvent, dir: ResizeDir) => {
    e.stopPropagation();
    e.preventDefault();
    resizeState.current = {
      dir,
      startX: e.clientX, startY: e.clientY,
      startW: position.width, startH: position.height,
      startPosX: position.x, startPosY: position.y,
    };
    setIsResizing(true);
    (e.target as HTMLElement).setPointerCapture(e.pointerId);

    const onMove = (ev: PointerEvent) => {
      const s = resizeState.current;
      if (!s) return;
      const dx = ev.clientX - s.startX;
      const dy = ev.clientY - s.startY;
      let w = s.startW;
      let h = s.startH;
      if (s.dir.includes('e')) w = Math.max(MIN_W, s.startW + dx);
      if (s.dir.includes('w')) w = Math.max(MIN_W, s.startW - dx);
      if (s.dir.includes('s')) h = Math.max(MIN_H, s.startH + dy);
      if (s.dir.includes('n')) h = Math.max(MIN_H, s.startH - dy);

      // 'w'/'n' anchor the OPPOSITE edge: the left/top edge moves by exactly
      // the (clamped) change in width/height, so the right/bottom edge stays
      // put. Without this, dragging the west or north handle just grows the
      // table down/right regardless of which edge was actually pulled.
      const x = s.dir.includes('w') ? Math.round(s.startPosX + (s.startW - w)) : undefined;
      const y = s.dir.includes('n') ? Math.round(s.startPosY + (s.startH - h)) : undefined;

      onResize(id, Math.round(w), Math.round(h), x, y);
    };

    const onUp = () => {
      resizeState.current = null;
      setIsResizing(false);
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
    };

    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }, [id, onResize, position.width, position.height, position.x, position.y]);

  const dirs: ResizeDir[] = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];

  const isClipped = shape === 'triangle' || shape === 'hexagon';
  const clipPath =
    shape === 'triangle'
      ? 'polygon(50% 0%, 0% 100%, 100% 100%)'
      : shape === 'hexagon'
      ? 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)'
      : undefined;

  const surfaceStyle: React.CSSProperties = {
    userSelect: 'none',
    padding: '0.5rem',
    width: '100%',
    height: '100%',
    ...(clipPath
      ? {
          clipPath,
          borderRadius: 0,
          filter: isOutOfBounds
            ? 'drop-shadow(0 0 5px #dc2626) drop-shadow(0 0 3px #dc2626)'
            : undefined,
        }
      : { borderRadius: shape === 'round' ? '50%' : '2px' }),
  };

  const contentStyle: React.CSSProperties =
    shape === 'triangle' ? { paddingTop: '30%' } : {};

  return (
    <div
      ref={setNodeRef}
      style={{
        position: 'absolute',
        left: `${position.x}px`,
        top: `${position.y}px`,
        transform: `translate(${transform?.x ?? 0}px, ${transform?.y ?? 0}px) rotate(${position.rotation}deg)`,
        transformOrigin: 'center',
        zIndex: isActive ? 1000 : 1,
        touchAction: 'none',
        width: `${position.width}px`,
        height: `${position.height}px`,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* ── Buttons live in the OUTER wrapper so clip-path never hides them ── */}

      {/* Shape picker — top-left */}
      <div style={{ position: 'absolute', top: -12, left: -12, zIndex: 20 }}>
        <button
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); setShowShapeMenu((v) => !v); }}
          className="bg-white rounded-full p-1 shadow-md border border-gray-200 hover:bg-gray-50 block"
          title="Change shape"
        >
          <ShapeIcon s={shape} />
        </button>

        {showShapeMenu && (
          <div
            onPointerDown={(e) => e.stopPropagation()}
            className="absolute top-7 left-0 bg-white rounded-lg shadow-lg border border-gray-200 p-1 flex flex-col gap-0.5 min-w-[120px]"
            style={{ zIndex: 200 }}
          >
            {SHAPES.map((s) => (
              <button
                key={s}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  onShapeChange(id, s);
                  setShowShapeMenu(false);
                }}
                className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs w-full text-left hover:bg-gray-100 ${
                  shape === s ? 'bg-primary-50 text-primary-700 font-semibold' : 'text-gray-700'
                }`}
              >
                <ShapeIcon s={s} />
                <span className="capitalize">{s}</span>
                {shape === s && <span className="ml-auto">✓</span>}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Rotate — top-right */}
      {!isActive && !isResizing && (
        <button
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onRotate(id); }}
          className="absolute bg-white rounded-full p-1 shadow-md border border-gray-200 hover:bg-gray-50"
          style={{ top: -12, right: -12, zIndex: 20 }}
          title="Rotate 90°"
        >
          <RotateCcw className="h-3 w-3 text-gray-600" />
        </button>
      )}

      {/* Out-of-bounds badge — bottom-left (clipped shapes use drop-shadow instead) */}
      {isOutOfBounds && !isClipped && (
        <div
          className="absolute -bottom-3 -left-3 bg-red-600 rounded-full p-0.5 shadow-md pointer-events-none"
          title="Table exceeds layout area"
        >
          <AlertTriangle className="h-3 w-3 text-white" />
        </div>
      )}

      {/* ── Drag surface (may have clip-path) ── */}
      <div
        {...(isResizing ? {} : listeners)}
        {...attributes}
        onClick={(e) => { e.stopPropagation(); onClickTable?.(id); }}
        className={`card transition-shadow ${
          !isClipped && isOutOfBounds
            ? 'border-red-600 border-4'
            : !isClipped
            ? `border-2 ${config.bgColor}`
            : config.bgColor.split(' ')[0]
        } ${
          isActive ? 'cursor-grabbing shadow-xl' : isResizing ? 'cursor-default' : 'cursor-grab hover:shadow-md'
        }`}
        style={surfaceStyle}
      >
        <div className="flex flex-col items-center justify-center h-full" style={contentStyle}>
          <span className={isClipped ? 'text-sm' : 'text-base'}>{config.emoji}</span>
          {/* Triangle/hexagon clip the corners of this box, so a shorter stack
              (smaller text) is more likely to stay inside the shape's usable
              area — the hexagon in particular is narrowest at its top/bottom. */}
          <div className={`font-bold leading-tight ${isClipped ? 'text-xs' : 'text-sm'} ${config.color}`}>
            {table.tableNumber}
          </div>
          <div className={`text-gray-600 ${isClipped ? 'text-[10px]' : 'text-xs'}`}>{table.capacity} seats</div>
          {table.section && (
            <div className={`text-gray-500 truncate max-w-full px-1 ${isClipped ? 'text-[10px]' : 'text-xs'}`}>
              {table.section}
            </div>
          )}
        </div>
      </div>

      {/* Resize handles */}
      {isHovered && !isActive && dirs.map((dir) => (
        <div
          key={dir}
          style={handleStyle(dir, position.width, position.height)}
          onPointerDown={(e) => startResize(e, dir)}
        />
      ))}
    </div>
  );
}
